import styled from 'styled-components';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import useEditUserForm from './useEditUserForm';
import TextInput from '../../../../shared/FormStyles/TextInput.jsx';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import { Button, Label } from '../../../../shared/FormStyles';
import RadixSelect from '../../../../shared/RadixSelect/RadixSelect.jsx';

const InputContainer = styled.div`
  margin: 8px 0;
`;

const PopulationSegmentsContainer = styled.div`
  margin: 8px 0;
`;

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: '8px',
    border: `1px solid ${state.isFocused ? '#3e63dd' : '#dbdde0'}`,
    boxShadow: state.isFocused ? '0 0 0 1px #3e63dd' : 'none',
    minHeight: '42px',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '16px',
    fontWeight: 500,
    textAlign: 'left',
    '&:hover': {
      borderColor: state.isFocused ? '#3e63dd' : '#cacccf',
    },
  }),
  option: provided => ({
    ...provided,
    textAlign: 'left',
    fontSize: '16px',
  }),
  menu: provided => ({
    ...provided,
    textAlign: 'left',
  }),
  menuList: provided => ({
    ...provided,
    maxHeight: '180px',
  }),
  placeholder: provided => ({
    ...provided,
    color: '#cacccf',
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: '#e6f0ff',
    borderRadius: '4px',
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: '#3e63dd',
    padding: '1px 6px',
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: '#3e63dd',
    ':hover': {
      backgroundColor: '#d0e0ff',
      color: '#1f2d5c',
    },
  }),
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat';
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const EditUserForm = ({ user }) => {
  const {
    handleChange,
    handleSubmit,
    handleSegmentChange,
    populationSegments,
    values,
    inputError,
    handleBlur,
    disabledSubmit,
  } = useEditUserForm({ user });

  const { t } = useTranslation(['users', 'shared']);

  return (
    <Form onSubmit={handleSubmit}>
      <InputContainer>
        <TextInput
          onChange={handleChange}
          value={values.name}
          name="name"
          label={t('users:userForm.name')}
          error={inputError('name')}
          onBlur={handleBlur}
          placeholder={t('users:userForm.namePlaceholder')}
        />
      </InputContainer>
      <InputContainer>
        <TextInput
          onChange={handleChange}
          value={values.employeeId}
          name="employeeId"
          label={t('users:userForm.employeeId')}
          error={inputError('employeeId')}
          onBlur={handleBlur}
          placeholder={t('users:userForm.employeeIdPlaceholder')}
        />
      </InputContainer>
      <InputContainer>
        <TextInput
          onChange={handleChange}
          value={values.email}
          name="email"
          label={t('users:userForm.email')}
          error={inputError('email')}
          onBlur={handleBlur}
          placeholder={t('users:userForm.emailPlaceholder')}
        />
      </InputContainer>
      <InputContainer>
        <TextInput
          onChange={handleChange}
          value={values.phoneNumber}
          name="phoneNumber"
          label={t('users:userForm.phone')}
          error={inputError('phoneNumber')}
          onBlur={handleBlur}
          placeholder={t('users:userForm.phonePlaceholder')}
        />
      </InputContainer>
      <InputContainer>
        <ToggleSwitch
          label={t('users:userForm.isAdmin')}
          onChange={handleChange}
          value={values.isAdmin}
          name="isAdmin"
          id="isAdmin"
          fontWeight="400"
          color="black.80"
          role="checkbox"
        />
      </InputContainer>
      {populationSegments.length > 0 && (
        <PopulationSegmentsContainer>
          <Label style={{ display: 'block', marginBottom: '8px' }}>
            {t('users:userForm.segments')}
          </Label>
          <RadixSelect
            onChange={handleSegmentChange}
            placeholder={t('users:userForm.segmentsPlaceholder')}
            value={values.segments}
            isClearable={true}
            options={populationSegments}
            isSearchable={true}
            isMulti={true}
            styles={selectStyles}
          />
        </PopulationSegmentsContainer>
      )}
      <ButtonGroup>
        <Button variant="tertiary" type="button" onClick={() => Swal.close()}>
          {t('shared:cancel')}
        </Button>
        <Button
          variant="containedBlue"
          ml="8px"
          type="submit"
          disabled={disabledSubmit}
        >
          {t('shared:save')}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default EditUserForm;
